import React, { useMemo, useEffect } from 'react';
import { prop } from 'ramda';
import { useDispatch } from 'react-redux';

import { Link } from '@interlayer/js/components/common/Link';
import { Portal } from '@interlayer/js/app/components/system/Portal';
import { SvgIcon } from '@interlayer/js/app/components/common/SvgIcon';

import { MENU_TYPES } from './constants';
import { TBottomMenuItemProps, TBottomMenuProps } from './types';
import { clsx } from '@interlayer/js/app/utils/clsx.util';
import { tagTypeService } from '@core/js/services/tagType.service';
import { useAppSelector, usePageSelector } from '@core/js/store/hooks';
import { useInMobile } from '@interlayer/js/app/hooks/device/hooks/useMediaQuery';
import { useShowAction } from '@interlayer/js/components/system/App/buzz/hooks/useShowAction';
import { TAG_TYPE_BUZZ } from '@interlayer/js/components/system/TagManager/buzz/constants';
import { useGameCodeLaunch } from '@interlayer/js/app/components/main/Games/hooks/useGameLaunch';
import { BOTTOM_STICKY_IDS } from '@interlayer/js/app/components/system/BottomStickyElements/constants';
import { setFooterClassName } from '@interlayer/js/components/system/Page/store/buzz/actions';

import style from './BottomMenu.scss';

const MenuItem: React.FC<TBottomMenuItemProps> = ({ type, link, text, icon, gameCode = '' }) => {
    const gameLaunch = useGameCodeLaunch(gameCode);
    const content = useMemo(() => {
        return (
            <>
                <SvgIcon
                    type={icon}
                    width="1.375rem"
                    height="1.375rem"
                    className={style.icon}
                />

                {text}
            </>
        );
    }, [icon, text]);

    if (type === MENU_TYPES.game) {
        return (
            <span
                className={style.link}
                onClick={gameLaunch}
            >
                  {content}
            </span>
        );
    }

    return (
        <Link
            key={link}
            href={link}
            className={style.link}
        >
            {content}
        </Link>
    );
};

export const BottomMenu: React.FC<TBottomMenuProps> = ({ config: { menuItem } }) => {
    const isMobile = useInMobile();
    const isShowPlayBingoButton = useAppSelector(prop('isShowPlayBingoButton'));
    const isStickyFooter = usePageSelector(prop('isStickyFooter'));
    const dispatch = useDispatch();
    const isShowBottomMenu = useMemo(() => {
        return isMobile && Boolean(menuItem.length);
    }, [isMobile, menuItem.length]);

    useShowAction(TAG_TYPE_BUZZ.bottomNavigation, isShowBottomMenu);

    useEffect(() => {
        dispatch(setFooterClassName(isShowBottomMenu ? 'withBottomMenu' : ''));

        return () => {
            dispatch(setFooterClassName(''));
        };
    }, [dispatch, isShowBottomMenu]);

    if (!isShowBottomMenu) {
        return null;
    }

    return (
        <Portal id={BOTTOM_STICKY_IDS.bottomMenu}>
            <div
                data-testid="BottomMenu_wrapper"
                className={clsx(style.wrapper, {
                    [style.isFooterSticky]: !isStickyFooter,
                    [style.withPlayButton]: isShowPlayBingoButton
                })}
            >
                {
                    menuItem.slice(0, 5).map((menuItemProps) => {
                        return <MenuItem key={menuItemProps.link || menuItemProps.gameCode} {...menuItemProps} />;
                    })
                }
            </div>
        </Portal>
    );
};

tagTypeService.registerTagType({ [TAG_TYPE_BUZZ.bottomNavigation]: BottomMenu });
